import { useState } from 'react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { useBrandArticles, useCreateBrandArticle, useDeleteBrandArticle, useSubscriptions } from '../hooks/useApi'
import { canUseFeature, normalizePlanId, getPlanDisplayName } from '../lib/plans'
import { Link } from 'react-router-dom'
import { Newspaper, Plus, Trash2, Lock } from 'lucide-react'

export default function BrandArticles() {
  const { data: subscriptions } = useSubscriptions()
  const planId = normalizePlanId(subscriptions?.[0]?.plan)
  const planName = getPlanDisplayName(subscriptions?.[0]?.plan)
  const canWrite = canUseFeature(planId, 'articles')

  const { data: articles = [], isLoading } = useBrandArticles()
  const createArticle = useCreateBrandArticle()
  const deleteArticle = useDeleteBrandArticle()

  const [form, setForm] = useState({ title: '', excerpt: '', content: '' })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await createArticle.mutateAsync(form)
    setForm({ title: '', excerpt: '', content: '' })
  }

  if (!canWrite) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Artikel Usaha</h1>
        <p className="text-gray-600 mb-6">
          Fitur artikel usaha tersedia untuk paket <strong>Basic</strong> dan <strong>Premium</strong>.
          Paket Anda saat ini: <strong>{planName}</strong>.
        </p>
        <Link to="/subscription">
          <Button>Upgrade Paket</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
        <Newspaper className="w-8 h-8" />
        Artikel Usaha
      </h1>
      <p className="text-gray-600 mb-8">
        Tulis artikel tentang usaha Anda agar pelanggan lebih mengenal bisnis Anda.
      </p>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Tulis Artikel Baru
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder="Judul artikel"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <Input
              placeholder="Ringkasan singkat (opsional)"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            />
            <textarea
              className="w-full border rounded-lg p-3 min-h-[160px] text-sm"
              placeholder="Isi artikel..."
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              required
            />
            <Button type="submit" disabled={createArticle.isPending}>
              {createArticle.isPending ? 'Menyimpan...' : 'Publikasikan Artikel'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <h2 className="text-xl font-semibold mb-4">Artikel Anda</h2>
      {isLoading ? (
        <p className="text-gray-500">Memuat...</p>
      ) : articles.length === 0 ? (
        <p className="text-gray-500">Belum ada artikel. Tulis artikel pertama Anda!</p>
      ) : (
        <ul className="space-y-4">
          {articles.map((a: any) => (
            <Card key={a.id}>
              <CardContent className="pt-6 flex justify-between items-start gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{a.title}</h3>
                  {a.excerpt && <p className="text-sm text-gray-600 mt-1">{a.excerpt}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(a.createdAt).toLocaleDateString('id-ID')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteArticle.mutate(a.id)}
                  aria-label="Hapus artikel"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </ul>
      )}
    </div>
  )
}
